/**
 * App.js - Root Application Component
 *
 * @description
 * Main application entry point with Wristband authentication integration.
 * Routes to role-specific layouts (MerchantLayout, SOLayout, ChannelPartnerLayout).
 * Superadmin users are redirected to admin.lynx.build.
 * Channel Partner admin chrome is on admin.{slug}.staging.lynx.build (LYN-309).
 * BrandingProvider wraps above AuthProvider for tenant-aware theming.
 *
 * @dependencies
 * - BootSplashDismisser: hide HTML #boot-splash after ready UI commits
 *
 * @notes
 * - Branding/unsigned boot returns null so the index.html sibling overlay
 *   keeps spinning. Do not remount a React splash — that restarts the wheel.
 * - App must not dismiss chrome on `/`, `/sign-in`, `/sign-up`.
 * - /sign-in clears missing-alias banner dismiss so the next login shows it again.
 * - /merchant/company/settings redirects to /merchant/company#merchant-alias (modal).
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, SignedOut, useUser, useAuth } from './contexts/AuthContext';
import { BrandingProvider, useBranding } from './contexts/BrandingContext';

// Page components
import Merchants from './components/Merchants';
import MerchantProfile from './components/MerchantProfile';
import Transactions from './components/Transactions';
import TransactionDetails from './components/TransactionDetails';
import Reporting from './components/Reporting';
import InvoiceForm from './components/invoices/InvoiceForm';
import InvoiceDetails from './components/invoices/InvoiceDetails';
import PaymentRecorder from './components/invoices/PaymentRecorder';
import EstimateForm from './components/estimates/EstimateForm';
import EstimateDetails from './components/estimates/EstimateDetails';
import CreditMemosPage from './components/creditMemos/CreditMemosPage';
import MerchantDashboard from './components/MerchantDashboard';
import SODashboard from './components/SODashboard';
// Auth components (Phase 2)
import { SignIn, SignUp, ForgotPassword, ResetPassword, AcceptInvite, AcceptCompanyInvite, AuthCallback } from './components/auth';
import { BootSplashDismisser, BootSplashKeeper, UnsignedSignInRedirect, shouldDismissBootSplashFromApp } from './components/auth/AuthBootSplash';
import Customers from './components/Customers';
import CustomerDetails from './components/CustomerDetails';
import Products from './components/Products';
import Company from './components/company/Company';
import WebsitePage from './components/website/WebsitePage';
import { COMPANY_SETTINGS_ALIAS_HREF } from './utils/merchantAliasDeepLink';
import CreateCompanyPage from './components/company/CreateCompanyPage';
import ProtectedRoute from './components/ProtectedRoute';
import Onboarding from './components/Onboarding';
import UserProfileWrapper from './components/UserProfileWrapper';
import DashboardSkeleton from './components/DashboardSkeleton';
import PackResolvingShell from './components/layouts/so/PackResolvingShell';
import { isSoAppPath } from './utils/packChromeHint';
// import EsignTemplate from './pages/EsignTemplate'; // Commented out - package @lynxtechnologies/esigntemplate not installed
import GlobalSettingsPage from './components/settings/GlobalSettingsPage';
import OutreachPage from './components/outreach/OutreachPage';
import ShortLinkRedirect from './components/ShortLinkRedirect';

// Layout components - each role has its own self-contained layout
import MerchantLayout from './components/layouts/merchant/MerchantLayout';
import SOLayout from './components/layouts/so/SOLayout';
import ChannelPartnerLayout from './components/layouts/channel-partner/ChannelPartnerLayout';
import ChannelPartnerDashboard from './pages/channel-partner/Dashboard';
import ChannelPartnerMyCompany from './pages/channel-partner/MyCompany';
import ChannelPartnerSettings from './pages/channel-partner/Settings';
import ChannelPartnerPlaceholder from './pages/channel-partner/Placeholder';
import ChannelPartnerIntegrations from './pages/channel-partner/Integrations';
import ChannelPartnerAdminHostRequired from './pages/channel-partner/AdminHostRequired';
import SoCompanyPage from './pages/so/SoCompanyPage';
import SoSettingsPage from './pages/so/SoSettingsPage';
import SoConnectorsPage from './pages/so/SoConnectorsPage';
import AssetsCrewPage from './pages/so/AssetsCrewPage';
import SoEmployeesPage from './pages/so/SoEmployeesPage';
import PackLynxPayRedirect from './components/layouts/so/PackLynxPayRedirect';
import { isChannelPartnerAdminHost } from './utils/lynxBuildHostname';

import apiClient from './services/api/apiClient';
import { hasValidStoredSession } from './utils/authHelpers';
import { clearBannerAuthSession } from './utils/missingAliasBannerDismiss';
import './App.css';

// Header hide threshold for auth pages
const HEADER_HIDE_SCROLL_THRESHOLD = 10;

/**
 * App - Root component with Router
 */
function App() {
  return (
    <Router>
      <Root />
    </Router>
  );
}

/**
 * Root - Branding + Auth provider wrapper
 * BrandingProvider wraps above AuthProvider so branding is available pre-auth
 */
function Root() {
  return (
    <BrandingProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrandingProvider>
  );
}

/**
 * AppContent - Main application content
 *
 * This component is now primarily a ROUTER:
 * - Routes to role-specific layouts (MerchantLayout, SOLayout)
 * - Handles auth pages (sign-in, sign-up) with minimal header
 * - API authentication setup
 * - SO merchant list fetch for Merchants / MerchantProfile routes
 *
 * Layout/sidebar/header logic is delegated to role-specific layouts.
 * Transaction polling lives only on MerchantDashboard (visible-tab, single interval).
 */
function AppContent() {
  const { isSignedIn, user: currentUser, isLoaded } = useUser();
  const { getToken } = useAuth();
  const { branding, isDefault, isLoading: isBrandingLoading } = useBranding();
  const [users, setUsers] = useState([]);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const activeTab = query.get('tab') || 'overview';

  const platformName = branding.identity.platformName;

  // Route checks - include all auth-related routes
  const isAuthRoute = useMemo(
    () => location.pathname.startsWith('/sign-in') ||
          location.pathname.startsWith('/sign-up') ||
          location.pathname.startsWith('/forgot-password') ||
          location.pathname.startsWith('/reset-password') ||
          location.pathname.startsWith('/accept-invite') ||
          location.pathname.startsWith('/accept-company-invite') ||
          location.pathname.startsWith('/auth/callback'),
    [location.pathname]
  );
  const isSignInRoute = useMemo(
    () => location.pathname.startsWith('/sign-in'),
    [location.pathname]
  );
  const isSignUpRoute = useMemo(
    () => location.pathname.startsWith('/sign-up'),
    [location.pathname]
  );

  // API authentication setup
  useEffect(() => {
    const setupApiAuth = async () => {
      if (!isLoaded || !isSignedIn || !currentUser || !getToken) return;

      try {
        const token = await getToken();

        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const fetchUsers = async () => {
          try {
            const response = await apiClient.get('/users');
            setUsers(response.data);
          } catch (err) {
            console.error('Failed to fetch users:', err);
          }
        };

        fetchUsers();
      } catch (err) {
        console.error('Error setting up API authentication:', err);
      }
    };

    setupApiAuth();
  }, [isLoaded, isSignedIn, currentUser, getToken]);

  // Next login should see the alias banner again. Clear on /sign-in — not in
  // AuthContext — so token persist cannot throw because of banner storage.
  useEffect(() => {
    if (location.pathname.startsWith('/sign-in')) {
      clearBannerAuthSession();
    }
  }, [location.pathname]);

  // Header visibility for auth routes (scroll to hide) — LynxPay only
  const handleAuthScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    setIsHeaderVisible(currentScrollY <= HEADER_HIDE_SCROLL_THRESHOLD);
  }, []);

  useEffect(() => {
    if (!isAuthRoute || !isDefault) {
      setIsHeaderVisible(true);
      return;
    }

    window.addEventListener('scroll', handleAuthScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleAuthScroll);
  }, [isAuthRoute, isDefault, handleAuthScroll]);

  // Gate on branding resolution FIRST — prevents LynxPay defaults from flashing
  // on white-labeled SO subdomains before tenant branding loads.
  // HTML #boot-splash (sibling of #root) stays up; do not paint a React splash.
  if (isBrandingLoading) {
    return <BootSplashKeeper />;
  }

  // Show loading state while auth is initializing (protected routes only).
  // Auth routes (/sign-in, /sign-up, etc.) manage their own loading states
  // via isCheckingAuth — don't block them behind the App-level gate.
  if (!isLoaded && !isAuthRoute) {
    // Only show dashboard skeleton if token exists AND has not expired.
    // Expired tokens (e.g., user left tab open overnight) keep HTML chrome —
    // auth will clear the stale session and redirect to /sign-in.
    if (hasValidStoredSession()) {
      return (
        <>
          <BootSplashDismisser ready />
          {isSoAppPath(location.pathname) ? <PackResolvingShell /> : <DashboardSkeleton />}
        </>
      );
    }

    // Unsigned / or protected path: keep or restore HTML chrome. Do not dismiss.
    return <BootSplashKeeper />;
  }

  return (
    <div className={`flex min-h-screen flex-col overflow-x-hidden ${isAuthRoute ? 'bg-auth-bg' : ''}`}>
      <BootSplashDismisser ready={shouldDismissBootSplashFromApp(location.pathname, { isSignedIn })} />
      {/* Auth route header — LynxPay only (tenants get nav from AuthLayout). */}
      {isAuthRoute && isDefault && !isBrandingLoading && (
        <header className={`fixed top-0 left-0 right-0 z-20 bg-auth-bg lg:bg-transparent transition-transform duration-300 ease-in-out ${
          isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex-shrink-0">
                <span className="text-xl font-bold text-brand">{platformName}</span>
              </Link>
              <nav className="flex items-center gap-3">
                <Link
                  to="/sign-in"
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
                    isSignInRoute
                      ? 'bg-brand text-gray-900 shadow-sm'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  Sign In
                </Link>
                <Link
                  to="/sign-up"
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
                    isSignUpRoute
                      ? 'bg-brand text-gray-900 shadow-sm'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  Sign Up
                </Link>
              </nav>
            </div>
          </div>
        </header>
      )}

      {/* Main content */}
      <main className={`flex-1 ${isAuthRoute && isDefault ? 'pt-16' : ''}`}>
        {!isAuthRoute && (
          <SignedOut>
            <div className="text-center py-20">
              <h2 className="text-3xl font-bold mb-4 text-slate-900">Welcome to {platformName}</h2>
              <p className="text-gray-600 mb-8">Please sign in to continue.</p>
              <Link to="/sign-in" className="bg-brand text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-brand-hover transition-colors">
                Sign In
              </Link>
            </div>
          </SignedOut>
        )}

        <Routes>
          {/* Auth routes */}
          <Route path="/sign-in/*" element={<SignIn />} />
          <Route path="/sign-up/*" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/accept-company-invite" element={<AcceptCompanyInvite />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Short link redirect (transactional email URLs) */}
          <Route path="/r/:shortCode" element={<ShortLinkRedirect />} />

          {/* Public pages */}
          <Route path="/privacy" element={
            <div className="max-w-4xl mx-auto py-8 px-4">
              <h1 className="text-2xl font-bold mb-4">Privacy Policy</h1>
              <p>Coming soon...</p>
            </div>
          } />
          <Route path="/terms" element={
            <div className="max-w-4xl mx-auto py-8 px-4">
              <h1 className="text-2xl font-bold mb-4">Terms & Conditions</h1>
              <p>Coming soon...</p>
            </div>
          } />

          {/* Onboarding (accessible after signup) */}
          <Route path="/onboarding" element={<Onboarding />} />

          {/* Test eSign Template - Commented out until @lynxtechnologies/esigntemplate package is ready */}
          {/* <Route path="/test-esign-template" element={<EsignTemplate />} /> */}

          {/* Dashboard redirect based on role */}
          <Route element={<ProtectedRoute />}>
            <Route
              path="/dashboard"
              element={
                (() => {
                  // ProtectedRoute handles !isLoaded case with skeleton
                  // This is a fallback for edge cases
                  if (!isLoaded || !isSignedIn) {
                    return null; // ProtectedRoute will show skeleton
                  }

                  const userRole = currentUser?.role;

                  // Show skeleton while waiting for role data
                  if (!userRole) {
                    return <DashboardSkeleton />;
                  }

                  switch (userRole) {
                    case 'merchant':
                      return <Navigate to="/merchant/dashboard" replace />;
                    case 'so':
                      return <Navigate to="/so/dashboard" replace />;
                    case 'superadmin':
                      window.location.replace(process.env.REACT_APP_ADMIN_URL || 'https://admin.lynx.build');
                      return <DashboardSkeleton />;
                    case 'channel_partner':
                      return <Navigate to="/cp/dashboard" replace />;
                    default:
                      return <div>Role not recognized. Please contact support.</div>;
                  }
                })()
              }
            />
          </Route>

          {/* ============================================ */}
          {/* MERCHANT ROUTES - Inside MerchantLayout     */}
          {/* ============================================ */}
          <Route element={<ProtectedRoute requiredRole="merchant" />}>
            <Route element={<MerchantLayout />}>
              <Route path="/merchant/dashboard" element={<MerchantDashboard activeTab={activeTab} />} />
              <Route path="/merchant/transactions" element={<Transactions />} />
              <Route path="/merchant/transactions/:transactionId" element={<TransactionDetails />} />
              <Route path="/merchant/reporting" element={<Reporting user={currentUser} />} />
              <Route path="/merchant/customers" element={<Customers />} />
              <Route path="/merchant/customers/:customerId" element={<CustomerDetails />} />
              <Route path="/merchant/products" element={<Products />} />
              <Route path="/merchant/company" element={<Company />} />
              <Route
                path="/merchant/company/settings"
                element={<Navigate to={COMPANY_SETTINGS_ALIAS_HREF} replace />}
              />
              <Route path="/merchant/website" element={<WebsitePage />} />
              <Route path="/merchant/balances" element={<div className="p-4"><h1 className="text-2xl font-bold">Balances</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
              <Route path="/merchant/invoices/new" element={<InvoiceForm />} />
              <Route path="/merchant/invoices/:invoiceNumber" element={<InvoiceDetails />} />
              <Route path="/merchant/invoices/:invoiceNumber/edit" element={<InvoiceForm />} />
              <Route path="/merchant/invoices/:invoiceNumber/payment" element={<PaymentRecorder />} />
              <Route path="/merchant/estimates/new" element={<EstimateForm />} />
              <Route path="/merchant/estimates/:estimateNumber" element={<EstimateDetails />} />
              <Route path="/merchant/estimates/:estimateNumber/edit" element={<EstimateForm />} />
              <Route path="/merchant/outreach" element={<OutreachPage />} />
              <Route path="/merchant/outreach/:tab" element={<OutreachPage />} />
              <Route path="/merchant/credit-memos" element={<CreditMemosPage />} />
              <Route path="/merchant/create-company" element={<CreateCompanyPage />} />
              <Route path="/merchant/profile/*" element={<UserProfileWrapper />} />
              <Route path="/merchant/settings" element={<GlobalSettingsPage />} />
            </Route>
          </Route>

          {/* ============================================ */}
          {/* SO ROUTES - Inside SOLayout                 */}
          {/* ============================================ */}
          <Route element={<ProtectedRoute requiredRole="channel_partner" />}>
            {isChannelPartnerAdminHost(
              typeof window !== 'undefined' ? window.location.hostname : ''
            ) ? (
              <Route element={<ChannelPartnerLayout />}>
                <Route path="/cp/dashboard" element={<ChannelPartnerDashboard />} />
                <Route path="/cp/sales-orgs" element={<ChannelPartnerPlaceholder title="Sales Orgs" />} />
                <Route path="/cp/merchants" element={<ChannelPartnerPlaceholder title="Merchants" />} />
                <Route path="/cp/my-company" element={<ChannelPartnerMyCompany />} />
                <Route path="/cp/integrations" element={<ChannelPartnerIntegrations />} />
                <Route path="/cp/settings" element={<ChannelPartnerSettings />} />
              </Route>
            ) : (
              <Route path="/cp/*" element={<ChannelPartnerAdminHostRequired />} />
            )}
          </Route>

          <Route element={<ProtectedRoute requiredRole="so" />}>
            <Route element={<SOLayout />}>
              <Route path="/so/dashboard" element={<SODashboard />} />
              <Route path="/so/assets-crew" element={<AssetsCrewPage />} />
              <Route path="/so/employees" element={<SoEmployeesPage />} />
              <Route path="/so/employees/pending-matches" element={<SoEmployeesPage />} />
              <Route path="/so/company" element={<SoCompanyPage />} />
              <Route path="/so/transactions" element={<PackLynxPayRedirect><Transactions /></PackLynxPayRedirect>} />
              <Route path="/so/transactions/:transactionId" element={<PackLynxPayRedirect><TransactionDetails /></PackLynxPayRedirect>} />
              <Route path="/so/merchants" element={
                <PackLynxPayRedirect>
                  <Merchants mockUsers={users.filter(m => m.role === 'merchant' && m.parentId === currentUser?.id)} />
                </PackLynxPayRedirect>
              } />
              <Route path="/so/merchants/:userId" element={
                <PackLynxPayRedirect>
                  <MerchantProfile mockUsers={users.filter(m => m.role === 'merchant' && m.parentId === currentUser?.id)} />
                </PackLynxPayRedirect>
              } />
              <Route path="/so/reporting" element={<PackLynxPayRedirect><Reporting user={currentUser} /></PackLynxPayRedirect>} />
              <Route path="/so/profile/*" element={<UserProfileWrapper />} />
              <Route path="/so/settings" element={<SoSettingsPage />} />
              <Route path="/so/settings/connectors" element={<SoConnectorsPage />} />
            </Route>
          </Route>

          {/* Root redirect — unsigned `/` must restore chrome (same class as
              ProtectedRoute). ready=false on `/` does not unhide a dismissed overlay. */}
          <Route path="/" element={
            isSignedIn ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <UnsignedSignInRedirect />
            )
          } />
        </Routes>
      </main>
    </div>
  );
}

export default App;
